import { Popover, Spinner } from "@xola/ui-kit";
import { ColDef, ColumnState } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { SecondaryButton } from "common";
import { uniqueId } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { DigitalWaiver, Experience, PreferenceContext, PreferenceKey, Timeslot } from "types";
import { RefObject } from "@fullcalendar/core";
import { applyFlexToLastVisible } from "../../DashboardTable/gridUtils";
import { normalizePinned } from "../../DashboardTableConfiguration";
import { FilterMenuButton } from "../../Filters/FilterMenuButton";
import { CreateViewModal } from "../../Filters/SavedViewsButton";
import { CustomRosterViewMenuItem } from "./CustomRosterViewMenuItem";
import { defaultColumnDefsOrder, DefaultColumnDefsOrderInterface, getColumnDefs, RosterColumnId } from "./helpers";
import { useQuestionnairePreference, XOLA_DEFAULT_CONFIG_NAME } from "@/hooks/questionnaireFormHooks";
import { useSeller } from "@/hooks/useAccount";
import { useCollection } from "@/hooks/useCollection";
import { useVisibility } from "@/hooks/useVisibility";
import { useDashboardRosterStore } from "@/stores/dashboardFilterStore";
import { canViewWaivers } from "@/stores/globalStore";
import { DashboardRosterColumnsConfig, DashboardRosterPreference, RosterViewConfiguration } from "@/types/Dashboard";
import { reorder } from "@/utils/reorder";

interface CustomRosterViewMenuProps {
    gridRef: RefObject<AgGridReact>;
    experience: Experience;
    timeslot: Timeslot;
}
const columnsToHideInRosterMode = [RosterColumnId.TIME, RosterColumnId.PRODUCT];

export const CustomRosterViewMenu = ({ gridRef, experience, timeslot: initialTimeslot }: CustomRosterViewMenuProps) => {
    const seller = useSeller();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [savingViewId, setSavingViewId] = useState<number | string | null>(null);
    const [currentTimeslot, setCurrentTimeslot] = useState<Timeslot>(initialTimeslot);
    const [isTableRendered, setIsTableRendered] = useState(false);
    const [visible, { toggle, hide }] = useVisibility(false);
    const [viewId, setRosterView] = useDashboardRosterStore((state) => [state.selectedRosterView, state.setRosterView]);
    const [currentTableState, setCurrentTableState] = useState<ColDef[] | null>(null);

    if (gridRef?.current?.api && !isTableRendered) {
        setIsTableRendered(true);
    }

    useCollection<DigitalWaiver>("waiverTemplates", null, {
        enabled: canViewWaivers(),
    });

    const { preference, viewConfigurations, isLoading, setPreference, savePreference, isSaving } =
        useQuestionnairePreference([], () => setShowCreateModal(false));

    const selectedViewConfiguration = viewConfigurations.find((viewConfiguration) => viewConfiguration.id === viewId);
    const favoriteViewConfiguration = viewConfigurations.find((viewConfiguration) => viewConfiguration.favourite);
    const isDefaultConfigurationExists = viewConfigurations.find((viewConfiguration) => {
        return viewConfiguration.id === XOLA_DEFAULT_CONFIG_NAME;
    });

    const hideDefaultCols = useCallback(
        (viewConfig: RosterViewConfiguration) => {
            // If default config exists, hide no columns
            if (viewConfig.id === XOLA_DEFAULT_CONFIG_NAME) {
                return null;
            }

            // Since we don`t save all columns state, only visible, we should hide columns that we didn`t get from the
            // api
            const { data: columnDefs } = getColumnDefs(viewConfigurations, experience);

            const filteredColumnDefs = columnDefs
                .filter((colDef) => !viewConfig.columns.some((newColumn) => newColumn.id === colDef.colId))
                .map((col) => ({ colId: col.colId!, hide: true }));

            gridRef?.current?.api.applyColumnState({
                state: filteredColumnDefs,
            });
        },
        [gridRef, experience, viewConfigurations],
    );

    const shouldUpdateColumns = favoriteViewConfiguration && !viewId && isTableRendered;

    const applyColumnState = useCallback(
        (columns: Array<ColDef | DashboardRosterColumnsConfig>) => {
            if (!columns) {
                return;
            }
            const newColumnOrder: ColumnState[] = columns.map((column) => ({
                colId: "colId" in column ? column.colId ?? "" : (column as DashboardRosterColumnsConfig).id || "",
                sort: column.sort,
                hide: column.hide,
                pinned: normalizePinned(column.pinned),
            }));

            gridRef?.current?.api.applyColumnState({
                state: newColumnOrder,
                applyOrder: true,
            });
        },
        [gridRef],
    );

    const applyViewConfiguration = useCallback(
        (config: RosterViewConfiguration) => {
            applyColumnState(config.columns);
            hideDefaultCols(config);
            // Older views won't have these flags; default both sub-views on.
            useDashboardRosterStore.getState().setRosterSubViews({
                showAttendeeView: config.showAttendeeView ?? true,
                showEquipmentView: config.showEquipmentView ?? true,
            });
        },
        [applyColumnState, hideDefaultCols],
    );

    useEffect(() => {
        if (initialTimeslot.id !== currentTimeslot.id) {
            setCurrentTimeslot(initialTimeslot);
            if (viewId && gridRef?.current?.api) {
                const selectedConfig = viewConfigurations.find((config) => config.id === viewId);
                if (selectedConfig && selectedConfig.id !== XOLA_DEFAULT_CONFIG_NAME) {
                    applyViewConfiguration(selectedConfig);
                }
            }
        }
    }, [initialTimeslot, currentTimeslot.id, viewId, gridRef, applyViewConfiguration, viewConfigurations]);

    useEffect(() => {
        if (favoriteViewConfiguration?.id === XOLA_DEFAULT_CONFIG_NAME && !viewId) {
            useDashboardRosterStore.getState().setRosterView(XOLA_DEFAULT_CONFIG_NAME);
            return;
        }

        if (!shouldUpdateColumns) {
            return;
        }

        useDashboardRosterStore.getState().setRosterView(favoriteViewConfiguration.id);

        // This hack used to fix this issue https://xola01.atlassian.net/browse/X2-11499
        // There's some conflicts with default columns state which goes from useColumDefs on first table rendering
        // And if trying to hide columns there it leads to crash
        // Since it's prod issue let's leave this fix for a time to fix it, cos I spent a lot of time with it
        // Need to open separate dev story for refactoring it, I have some thoughts how it can be fixed in another way
        // but for it need to change what we save in columns state and it will require more testing and time.
        setTimeout(() => {
            applyColumnState(
                favoriteViewConfiguration.columns.map((col) => ({
                    ...col,
                    hide: columnsToHideInRosterMode.includes(col.id as RosterColumnId) ? true : col.hide ?? false,
                })),
            );
            hideDefaultCols(favoriteViewConfiguration);
        }, 100);
    }, [viewId, shouldUpdateColumns, favoriteViewConfiguration, gridRef, hideDefaultCols, applyColumnState]);

    const handleCreateViewConfiguration = async (name: string) => {
        const columnDefs = gridRef.current?.api.getColumnDefs() as ColDef[];
        const mappedColumnDefs = columnDefs?.map((colDef) => ({
            id: colDef.colId,
            sort: colDef.sort,
            hide: colDef.colId === RosterColumnId.PRODUCT || colDef.colId === RosterColumnId.TIME ? false : colDef.hide,
            pinned: colDef.pinned ?? null,
        }));
        const visibleColumns = mappedColumnDefs.filter((column) => !column.hide);

        const newId = Date.now();

        const { showAttendeeView, showEquipmentView } = useDashboardRosterStore.getState();
        const viewConfiguration = {
            id: newId,
            name,
            columns: visibleColumns,
            favourite: false,
            showAttendeeView,
            showEquipmentView,
        } as any;

        if (preference?.id) {
            // Preference exists, just update it.
            setPreference((preference) => {
                preference.value.configurations.push(viewConfiguration);
            });

            useDashboardRosterStore.setState({ selectedRosterView: newId });
            return;
        }

        // Create new preference.
        savePreference({
            seller: { id: seller.id },
            context: PreferenceContext.USER,
            key: PreferenceKey.XOLA_SELLER_ROSTER,
            value: { configurations: [viewConfiguration] },
        });

        setRosterView(newId);
        useDashboardRosterStore.setState({ selectedRosterView: newId });
    };

    const handleSaveItemClick = (viewConfiguration: RosterViewConfiguration) => {
        const colDefs = gridRef.current?.api.getColumnDefs() as ColDef[];

        const mappedColumnDefs =
            colDefs?.map((colDef) => ({
                id: colDef.colId,
                sort: colDef.sort ?? null,
                hide: colDef.hide ?? null,
                pinned: colDef.pinned ?? null,
            })) ?? [];

        const visibleColumns = mappedColumnDefs.filter((column) => !column.hide);

        const { showAttendeeView, showEquipmentView } = useDashboardRosterStore.getState();
        setPreference((preference) => {
            const updatedConfigurations = preference.value.configurations.map((config) => {
                if (config.id === viewConfiguration.id) {
                    return { ...config, columns: visibleColumns, showAttendeeView, showEquipmentView };
                }
                return config;
            });

            return {
                ...preference,
                value: { ...preference.value, configurations: updatedConfigurations },
            };
        });

        setSavingViewId(viewConfiguration.id);

        const updatedColDefs = gridRef.current?.api.getColumnDefs() as ColDef[];
        const updatedMappedForState: ColumnState[] = (updatedColDefs ?? []).map((colDef) => ({
            colId: colDef.colId!,
            sort: colDef.sort ?? null,
            hide: Boolean(colDef.hide),
            pinned: colDef.pinned ?? null,
        }));

        const stateWithFlex = applyFlexToLastVisible(updatedMappedForState);

        gridRef.current?.api.applyColumnState({
            state: stateWithFlex,
            applyOrder: false,
        });

        setCurrentTableState(updatedColDefs);
    };

    const handleItemToggleFavourite = (viewConfiguration: RosterViewConfiguration) => {
        // Allows us to toggle the favourite.
        const favouriteId = viewConfiguration.favourite ? null : viewConfiguration.id;

        setPreference((preference: DashboardRosterPreference) => {
            for (const configuration of preference.value.configurations) {
                configuration.favourite = configuration.id === favouriteId;
            }
        });
    };

    const handleItemClick = (viewConfiguration: RosterViewConfiguration) => {
        if (!gridRef.current) return;

        const newColumnOrder: ColumnState[] =
            viewConfiguration.id === XOLA_DEFAULT_CONFIG_NAME
                ? defaultColumnDefsOrder.map((colDef) => ({
                      colId: colDef.colId!,
                      sort: colDef.sort,
                      hide: columnsToHideInRosterMode.includes(colDef.colId as RosterColumnId) ? true : colDef.hide,
                      pinned: normalizePinned(colDef.pinned),
                  }))
                : viewConfiguration.columns.map((column) => ({
                      colId: column.id,
                      sort: column.sort,
                      hide: columnsToHideInRosterMode.includes(column.id as RosterColumnId) ? true : column.hide,
                      pinned: normalizePinned(column.pinned),
                  }));

        const stateWithFlex = applyFlexToLastVisible(newColumnOrder);

        gridRef.current.api.applyColumnState({
            state: stateWithFlex,
            applyOrder: true,
        });

        setRosterView(viewConfiguration.id);
        hideDefaultCols(viewConfiguration);
    };

    const handleItemDelete = (viewConfiguration: RosterViewConfiguration) => {
        const newFavoriteId =
            viewConfiguration.favourite && favoriteViewConfiguration?.id === viewConfiguration.id
                ? XOLA_DEFAULT_CONFIG_NAME
                : favoriteViewConfiguration?.id;

        setPreference((preference: DashboardRosterPreference) => {
            const configurations = preference.value.configurations.filter((configuration) => {
                return configuration.id !== viewConfiguration.id;
            });

            for (const configuration of configurations) {
                configuration.favourite = configuration.id === newFavoriteId;
            }

            preference.value.configurations = configurations;
        });

        if (viewId === viewConfiguration.id) {
            const favoriteView = viewConfigurations.find((view) => view.favourite);

            if (favoriteView && favoriteView.id !== viewConfiguration.id) {
                const columns = favoriteView.columns.map((column) => ({
                    colId: column.id,
                    sort: column.sort,
                    hide: column.hide ?? columnsToHideInRosterMode.includes(column.id as RosterColumnId),
                    pinned: normalizePinned(column.pinned),
                }));

                const stateWithFlex = applyFlexToLastVisible(columns);

                gridRef?.current?.api.applyColumnState({
                    state: stateWithFlex,
                    applyOrder: true,
                });

                setRosterView(favoriteView.id);
                return;
            }

            gridRef?.current?.api.applyColumnState({
                state: defaultColumnDefsOrder as ColumnState[],
                applyOrder: true,
            });

            setRosterView(XOLA_DEFAULT_CONFIG_NAME);
        }
    };

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const reOrderedItems = reorder(viewConfigurations, result.source.index, result.destination.index);

        setPreference((preference) => {
            preference.value.configurations = reOrderedItems;
        });
    };

    useEffect(() => {
        if (isTableRendered) {
            const columnDefs = gridRef.current?.api.getColumnDefs() as DefaultColumnDefsOrderInterface[];
            const mappedColumnDefs = columnDefs?.map((colDef) => ({
                colId: colDef.colId,
                sort: colDef.sort ?? null,
                hide: Boolean(colDef.hide) ?? null,
                pinned: normalizePinned(colDef.pinned),
            }));

            setCurrentTableState(mappedColumnDefs);
        }
    }, [visible, viewId, gridRef, isTableRendered, setCurrentTableState]);

    useEffect(() => {
        if (savingViewId) {
            const timer = setTimeout(() => {
                setSavingViewId(null);
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [savingViewId]);

    useEffect(() => {
        const isOldConfiguration =
            isDefaultConfigurationExists &&
            viewConfigurations.find((config) => config.id === XOLA_DEFAULT_CONFIG_NAME && config.columns);

        if (!isDefaultConfigurationExists || isOldConfiguration) {
            const defaultConfiguration = {
                id: XOLA_DEFAULT_CONFIG_NAME,
                name: "Xola Default",
                favourite: true,
            } as any;

            if (preference?.id) {
                const mappedViewConfigurations = viewConfigurations.map((config) => {
                    return config.id === XOLA_DEFAULT_CONFIG_NAME ? defaultConfiguration : config;
                });

                setPreference((preference) => {
                    preference.value.configurations = mappedViewConfigurations;
                });

                setRosterView(XOLA_DEFAULT_CONFIG_NAME);
                return;
            }

            savePreference({
                seller: { id: seller.id },
                context: PreferenceContext.USER,
                key: PreferenceKey.XOLA_SELLER_ROSTER,
                value: { configurations: [defaultConfiguration] },
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, isDefaultConfigurationExists]);

    return (
        <div className="ui-custom-view-menu inline-block">
            {showCreateModal && (
                <CreateViewModal
                    isOpen={showCreateModal}
                    isLoading={isSaving}
                    onClose={() => setShowCreateModal(false)}
                    onSubmit={handleCreateViewConfiguration}
                />
            )}

            <Popover
                open={visible}
                maxWidth={300}
                offset={[0, 18]}
                zIndex={20}
                placement="auto"
                trigger="click"
                onClickOutside={hide}
            >
                <FilterMenuButton
                    isDropdown
                    text={selectedViewConfiguration?.name ?? "Xola Default"}
                    onClick={toggle}
                />

                <Popover.Content>
                    {isLoading ? (
                        <Spinner className="ui-custom-view-menu-loader" />
                    ) : (
                        <div className="min-w-[300px]">
                            <h3 className="p-5 pt-3 pb-3">
                                Saved Views{" "}
                                {isSaving ? (
                                    <Spinner className="ui-custom-view-menu-inline-loader" size="tiny" />
                                ) : null}
                            </h3>

                            <div className="flex items-center bg-[#F0F2F4] p-5 pt-2 pb-2">
                                <span className="text-base font-bold">Private Views</span>
                            </div>

                            {visible && (
                                <DragDropContext onDragEnd={handleDragEnd}>
                                    <Droppable
                                        droppableId="private_views"
                                        renderClone={(provided, _, rubric) => (
                                            <div
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                ref={provided.innerRef}
                                            >
                                                <CustomRosterViewMenuItem
                                                    viewConfiguration={viewConfigurations[rubric.source.index]}
                                                    currentTableState={currentTableState}
                                                    isActive={viewConfigurations[rubric.source.index].id === viewId}
                                                    systemHiddenColumns={columnsToHideInRosterMode}
                                                    draggableProvided={provided}
                                                    isSaving={
                                                        viewConfigurations[rubric.source.index].id === savingViewId
                                                    }
                                                    isDefaultItem={
                                                        viewConfigurations[rubric.source.index].id ===
                                                        XOLA_DEFAULT_CONFIG_NAME
                                                    }
                                                    onToggleFavorite={handleItemToggleFavourite}
                                                    onSaveItemClick={handleSaveItemClick}
                                                    onClick={handleItemClick}
                                                    onDelete={handleItemDelete}
                                                />
                                            </div>
                                        )}
                                    >
                                        {(provided) => (
                                            <div
                                                ref={provided.innerRef}
                                                className="flex h-full max-h-75 flex-col overflow-auto"
                                                {...provided.droppableProps}
                                            >
                                                {viewConfigurations.map((viewConfiguration, index) => (
                                                    <Draggable
                                                        key={uniqueId("id-")}
                                                        draggableId={`id-${index}`}
                                                        index={index}
                                                    >
                                                        {(provided) => (
                                                            <div ref={provided.innerRef} {...provided.draggableProps}>
                                                                <CustomRosterViewMenuItem
                                                                    viewConfiguration={viewConfiguration}
                                                                    currentTableState={currentTableState}
                                                                    isActive={viewConfiguration.id === viewId}
                                                                    systemHiddenColumns={columnsToHideInRosterMode}
                                                                    draggableProvided={provided}
                                                                    isSaving={viewConfiguration.id === savingViewId}
                                                                    isDefaultItem={
                                                                        viewConfiguration.id ===
                                                                        XOLA_DEFAULT_CONFIG_NAME
                                                                    }
                                                                    onToggleFavorite={handleItemToggleFavourite}
                                                                    onSaveItemClick={handleSaveItemClick}
                                                                    onClick={handleItemClick}
                                                                    onDelete={handleItemDelete}
                                                                />
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            )}

                            <div className="flex items-center bg-[#F0F2F4] p-5 pt-2 pb-2">
                                <span className="text-base font-bold">Shared Views</span>
                            </div>

                            <h3 className="flex justify-center p-5 pt-3 pb-3">Coming Soon!</h3>

                            <div className="space-y-2 border-t  border-t-gray-light p-4">
                                <div>
                                    <SecondaryButton className="w-full" onClick={() => setShowCreateModal(true)}>
                                        Save as New View
                                    </SecondaryButton>
                                </div>
                            </div>
                        </div>
                    )}
                </Popover.Content>
            </Popover>
        </div>
    );
};
